import { capitalizeWords } from '../../../ts-utils'

/** A full-stats sport record, as the sports store holds it. */
export interface FullStatsSportRecord {
  id: string | number
  name?: string | null
  name_full?: string | null
}

/** A basic-stats (generic) sport record, as the sports store holds it. */
export interface BasicStatsSportRecord {
  id: string | number
  name?: string | null
}

export interface SportSelectOption {
  value: string
  label: string
}

export interface SportSelectGroup {
  label: string
  options: SportSelectOption[]
}

/** What a sport pick resolves to; each caller maps it onto its own filter fields. */
export interface SportSelection {
  /** The picked id — a full-stats sport id, or a basic-stats sport id. */
  value: string
  /** True for a basic-stats (generic) sport. */
  isGeneric: boolean
  /**
   * The parent 'generic' full-stats sport — basic-stats positions are keyed
   * under it. '' when the store has none.
   */
  genericParentId: string
}

const byLabel = (a: SportSelectOption, b: SportSelectOption) =>
  a.label.localeCompare(b.label)

const isGenericParent = (sport: FullStatsSportRecord) =>
  Boolean(sport?.name?.toLowerCase().includes('generic'))

/** The parent 'generic' full-stats sport's id, or '' when there is none. */
export const getGenericParentSportId = (
  sports: FullStatsSportRecord[]
): string => {
  const parent = sports.find(isGenericParent)
  return parent ? String(parent.id) : ''
}

/**
 * Every sport, grouped the way the create-competition wizard and Team Finder
 * show them: Full Stats (the full-stats sports, minus the 'generic' parent),
 * then Basic Stats (the generic sports). Each group is alphabetical.
 */
export const buildSportSelectGroups = (
  sports: FullStatsSportRecord[],
  genericSports: BasicStatsSportRecord[]
): SportSelectGroup[] => [
  {
    label: capitalizeWords('Full Stats'),
    options: sports
      .filter((sport) => !isGenericParent(sport))
      .map((sport) => ({
        value: String(sport.id),
        label: sport.name_full ?? '',
      }))
      .sort(byLabel),
  },
  {
    label: capitalizeWords('Basic Stats'),
    options: genericSports
      .map((sport) => ({ value: String(sport.id), label: sport.name ?? '' }))
      .sort(byLabel),
  },
]

/** A select value → what was picked; null for an empty pick. */
export const resolveSportSelection = (
  sports: FullStatsSportRecord[],
  genericSports: BasicStatsSportRecord[],
  value: string | null | undefined
): SportSelection | null => {
  if (!value) {
    return null
  }
  return {
    value,
    isGeneric: genericSports.some((sport) => String(sport.id) === value),
    genericParentId: getGenericParentSportId(sports),
  }
}

/**
 * The select's search filter for grouped options: matches leaf options by
 * label, never the group headers.
 */
export const matchesSportOption = (
  input: string,
  option: { label?: unknown; options?: unknown } | undefined
): boolean => {
  if (Array.isArray(option?.options)) {
    return false
  }
  return String(option?.label ?? '')
    .toLowerCase()
    .includes(input.toLowerCase())
}
